import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import FundRequestTable from "../../component/FundRequestTable";
import {
  getFundRequestBanks,
  getPaymentModeByBank,
  uploadFundReceipt,
  submitFundRequest,
  getFundOrderReport,
} from "../../../services/panel/panelServices";
import toast from "../../../utils/toast";

const NON_FIELD_FLAGS = ["isActive", "isTransactionIdAuto"];

const getModeFields = (mode) =>
  mode
    ? Object.keys(mode).filter(
        (key) =>
          key.startsWith("is") &&
          mode[key] === true &&
          !NON_FIELD_FLAGS.includes(key),
      )
    : [];

// "isAccountHolderRequired" → "Account Holder"
const flagLabel = (flag) =>
  flag
    .replace(/^is/, "")
    .replace(/Required$/, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2");

// Payload keys are fixed — each one is filled from the input of its flag
const PAYLOAD_FLAG = {
  MobileNo: "isMobileNoRequired",
  AccountHolderName: "isAccountHolderRequired",
  ChequeNo: "isChequeNoRequired",
  UPIID: "isUPIID",
  Branch: "isBranchRequired",
  CardNo: "isCardNumberRequired",
  Cvv: "isCvv",
  CardExpiry: "isCardExpiry",
};

const EMPTY_FORM = { bankId: "", paymentId: "", amount: "", utr: "" };

const REPORT_TOP_ROWS = 50;

export default function AddFund() {
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [isBankData, setBankData] = useState([]);
  const [isPaymentModeData, setPaymentModeData] = useState([]);
  const [modeLoading, setModeLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  // Values of the dynamic inputs, keyed by flag name
  const [modeValues, setModeValues] = useState({});
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [isReportData, setReportData] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => {
    fetchBankList();
    fetchFundOrderReport();
  }, []);

  // Recent requests only — filters live on the Fund requests page
  const fetchFundOrderReport = async () => {
    setReportLoading(true);
    try {
      const payload = {
        FromDate: "",
        ToDate: "",
        TMode: 0,
        Status: 0,
        AccountNo: "",
        TransactionID: "",
        TopRows: REPORT_TOP_ROWS,
        IsRecent: true,
      };
      const res = await getFundOrderReport(payload);

      if (res?.statuscode === 1) {
        setReportData(res?.data || []);
      } else {
        setReportData([]);
      }
    } catch (err) {
      console.log(
        err.response?.data?.msg || "Failed to fetch fund order report",
        "error",
      );
    } finally {
      setReportLoading(false);
    }
  };

  const fetchBankList = async () => {
    try {
      const res = await getFundRequestBanks();

      if (res?.statuscode === 1) {
        setBankData(res?.data || []);
      }
    } catch (err) {
      console.log(err.response?.data?.msg || "Failed to fetch banks", "error");
    }
  };

  const fetchPaymentMode = async (bankId) => {
    setModeLoading(true);
    setPaymentModeData([]);
    try {
      const res = await getPaymentModeByBank(Number(bankId));

      if (res?.statuscode === 1) {
        setPaymentModeData(res?.data || []);
      }
    } catch (err) {
      console.log(
        err.response?.data?.msg || "Failed to fetch payment modes",
        "error",
      );
    } finally {
      setModeLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((er) => ({ ...er, [name]: "" }));
  };

  const handleBankChange = (e) => {
    const bankId = e.target.value;
    setForm((f) => ({ ...f, bankId, paymentId: "" }));
    setErrors((er) => ({ ...er, bankId: "", paymentId: "" }));
    if (bankId) fetchPaymentMode(bankId);
    else setPaymentModeData([]);
  };

  const handleModeFieldChange = (e) => {
    const { name, value } = e.target;
    setModeValues((v) => ({ ...v, [name]: value }));
    setErrors((er) => ({ ...er, [name]: "" }));
  };

  const selectedMode = isPaymentModeData.find(
    (m) => String(m.id) === String(form.paymentId),
  );
  const modeFields = getModeFields(selectedMode);
  const isUtrAuto = Boolean(selectedMode?.isTransactionIdAuto);
  // Only send a value when its input is shown for the selected method
  const fieldValue = (flag) =>
    modeFields.includes(flag) ? (modeValues[flag] || "").trim() : "";

  const validate = () => {
    const e = {};
    if (!form.bankId) e.bankId = "Select a bank.";
    if (!form.paymentId) e.paymentId = "Select a funding method.";
    if (!(Number(form.amount) > 0)) e.amount = "Enter a valid amount.";
    if (!isUtrAuto && !form.utr.trim())
      e.utr = "UTR / reference number is required.";
    modeFields.forEach((flag) => {
      if (!(modeValues[flag] || "").trim())
        e[flag] = `${flagLabel(flag)} is required.`;
    });
    if (!file) e.file = "Upload proof of transfer.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const uploadRes = await uploadFundReceipt(Number(form.bankId), file);

      if (uploadRes?.statuscode !== 1) {
        toast.error(uploadRes?.msg || "Failed to upload receipt.");
        return;
      }

      const payload = {
        BankId: Number(form.bankId),
        PaymentModeId: Number(form.paymentId),
        Amount: Number(form.amount),
        TransactionId: isUtrAuto ? "" : form.utr.trim(),
        MobileNo: fieldValue(PAYLOAD_FLAG.MobileNo),
        AccountHolderName: fieldValue(PAYLOAD_FLAG.AccountHolderName),
        ChequeNo: fieldValue(PAYLOAD_FLAG.ChequeNo),
        UPIID: fieldValue(PAYLOAD_FLAG.UPIID),
        Branch: fieldValue(PAYLOAD_FLAG.Branch),
        CardNo: fieldValue(PAYLOAD_FLAG.CardNo),
        Cvv: fieldValue(PAYLOAD_FLAG.Cvv),
        CardExpiry: fieldValue(PAYLOAD_FLAG.CardExpiry),
        RImage: uploadRes?.data,
      };
      const res = await submitFundRequest(payload);
      if (res?.statuscode === 1) {
        toast.success(res?.msg);
        setForm(EMPTY_FORM);
        setModeValues({});
        setPaymentModeData([]);
        setFile(null);
        if (fileRef.current) fileRef.current.value = "";
        fetchFundOrderReport(); // show the new request
      } else {
        toast.error(res?.msg || "Failed to submit fund request.");
      }
    } catch (err) {
      toast.error(err.response?.data?.msg || "Failed to submit fund request.");
    } finally {
      setSubmitting(false);
    }
  };

  const errorText = (key) =>
    errors[key] ? (
      <span style={{ color: "#dc2626", fontSize: 11 }}>{errors[key]}</span>
    ) : null;
  const errorBorder = (key) => (errors[key] ? { borderColor: "#dc2626" } : {});

  return (
    <>
      <div className="screen active" id="screen-addfund">
        <div className="page-head">
          <div>
            <h1>Add fund</h1>
            <div className="ph-sub">
              Top up your approved balance for bill payments
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card-title">New fund request</div>
          <div className="card-sub">
            Funds are credited after CorpBills operations verifies the transfer
          </div>
          <div className="form-grid form-grid-4">
            <div className="field">
              <label>Bank</label>
              <select
                name="bankId"
                value={form.bankId}
                onChange={handleBankChange}
                style={errorBorder("bankId")}
              >
                <option value="">Select bank</option>
                {isBankData.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.bankName}
                  </option>
                ))}
              </select>
              {errorText("bankId")}
            </div>
            <div className="field">
              <label>Funding method</label>
              <select
                name="paymentId"
                value={form.paymentId}
                onChange={handleChange}
                disabled={!form.bankId || modeLoading}
                style={errorBorder("paymentId")}
              >
                <option value="">
                  {modeLoading
                    ? "Loading methods..."
                    : !form.bankId
                      ? "Select bank first"
                      : isPaymentModeData.length
                        ? "Select method"
                        : "No methods found"}
                </option>
                {isPaymentModeData.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.mode}
                  </option>
                ))}
              </select>
              {errorText("paymentId")}
            </div>
            <div className="field">
              <label>Amount</label>
              <input
                type="number"
                name="amount"
                min={1}
                value={form.amount}
                onChange={handleChange}
                placeholder="0"
                style={errorBorder("amount")}
              />
              {errorText("amount")}
            </div>
            {!isUtrAuto && (
              <div className="field">
                <label>UTR / reference number</label>
                <input
                  type="text"
                  name="utr"
                  value={form.utr}
                  onChange={handleChange}
                  placeholder="e.g. UTR2608290001"
                  style={errorBorder("utr")}
                />
                {errorText("utr")}
              </div>
            )}
            {modeFields.map((flag) => (
              <div className="field" key={flag}>
                <label>{flagLabel(flag)}</label>
                <input
                  type="text"
                  name={flag}
                  value={modeValues[flag] || ""}
                  onChange={handleModeFieldChange}
                  placeholder={`Enter ${flagLabel(flag).toLowerCase()}`}
                  style={errorBorder(flag)}
                />
                {errorText(flag)}
              </div>
            ))}
            <div className="field">
              <label>Proof of transfer</label>
              <input
                type="file"
                ref={fileRef}
                accept="image/*,application/pdf"
                onChange={(e) => {
                  setFile(e.target.files?.[0] || null);
                  setErrors((er) => ({ ...er, file: "" }));
                }}
                style={errorBorder("file")}
              />
              {errorText("file")}
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              type="button"
              className="btn btn-gold"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "Submit request"}
            </button>
          </div>
        </div>

        <div className="card">
          <div
            className="card-head-row"
            style={{ alignItems: "center", marginBottom: 18 }}
          >
            <div>
              <div className="card-title">Recent fund requests</div>
            
            </div>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => navigate("/fund-requests")}
            >
              View all
            </button>
          </div>
          <FundRequestTable rows={isReportData} loading={reportLoading} />
        </div>
      </div>
    </>
  );
}
